<template lang="pug">
.preview-image(ref="container")
    .info-box(v-if="tile.title || tile.text || finalLink && hyperlink.type === 'external'")
        a.item(
            v-if="path || href" 
            :href="finalLink" 
            :target="target" 
            ref="container" 
        )
            .bg
            .box
                .title(v-if="tile.title") {{ tile.title }}
                .desc(v-if="tile.text" v-html="tile.text")
                .href(v-if="finalLink && hyperlink.type === 'external'") {{ finalLink }}
        .item(v-else)
            .bg
            .box
                .title(v-if="tile.title") {{ tile.title }}
                .desc(v-if="tile.text" v-html="tile.text")
    .image-wrap(:style="{ width: `${image.width}px`}")
        lazy-image(
            v-if="image"
            v-lazy="image"
            rel="noopener"
            :src="image"
          )
    slot
</template>

<script>
import { get, trim, debounce, startsWith } from '@/helpers';

export default {
    name: 'Tile',
    data() {
        return {
            containerWidth: undefined
        };
    },
    props: {
        tile: {
            type: Object,
            required: true
        },
        maxWidth: {
            type: Number
        },
    },
    mounted() {
        if (this.id) {
            const state = this.getCollapseState();
            if (state) {
                this.isCollapsed = state[this.id];
            }
        }
        const calculateMaxWidth = () => {
            if (this.$refs.container) {
                const style = getComputedStyle(this.$refs.container);
                this.containerWidth = this.$refs.container.clientWidth;
                this.containerWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
            }
        };

        window.addEventListener('resize', debounce(calculateMaxWidth, 100));
        calculateMaxWidth();
    },
    computed:{
        sections() {
            return this.tile;
        },
        image() {
            const maxWidth = this.containerWidth;
            const maxHeight = 180;
            const image = get(this.tile, 'images.0');
            if (!image || !maxWidth) return false;

            let fit = get(image, 'pivot.fit') || 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1';
            const parameters = {
                // ...get(fit.match(/x=(?<fx>[0-1]?\.?[0-9]*)/), 'groups'),
                // ...get(fit.match(/y=(?<fy>[0-1]?\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/z=(?<fz>[1-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/w=(?<w>[0-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/h=(?<h>[0-9]+\.?[0-9]*)/), 'groups')
            };
            // let { w, h, fx, fy, fz } = parameters;
            let { w, h, fz } = parameters;
            if (!fz) fz = 1;

            let width, height, aspectRatio;

            width = image.width / fz;
            height = image.height / fz;
            aspectRatio = width / height;

            if (w && h) {
                aspectRatio = w / h;
                height = Math.min(maxWidth, width) / aspectRatio;


                fit = fit.replace(/w=([0-9]+\.?[0-9]*)/, `w=${width}`).replace(/h=([0-9]+\.?[0-9]*)/, `h=${height}`);
            } else if (!w && !h) {
                if (maxHeight < height) {
                    fit += `&h=${Math.min(maxHeight, Math.min(maxWidth, width) / aspectRatio)}`;
                    width = maxHeight * aspectRatio;
                } else {
                    fit += `&w=${Math.min(maxWidth, width)}`;  
                };
            }

            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );

            return {
                src: `${storageRoute}/images/${image.id}/${image.name}`,
                error: `${storageRoute}/images/${image.id}/${image.name}?review=greyscale`,
                loading: `${storageRoute}/images/${image.id}/${image.name}?preview=blur`,
                width,
                height,
                aspectRatio
            };
        },
        width() {
            const maxWidth = this.containerWidth;
            if (this.image.width) return Math.min(maxWidth, this.image.width);
            else return maxWidth;
        },
        height() {
            if (this.width && this.image.aspectRatio && this.image.aspectRatio ) return  this.width / this.image.aspectRatio;
            else return undefined;
        },

        hyperlink() {
            return get(this.tile, 'hyperlink') || {};
        },
        href() {
            let href = false;

            if (this.hyperlink.type !== 'internal' && get(this.hyperlink, 'href')) {
                const link = this.hyperlink.href;
                switch (this.hyperlink.type) {
                    case 'external':
                        if (!startsWith(link, 'https://') && !startsWith(link, 'http://')) href = `https://${link}`;
                        else href = link;
                        break;

                    case 'mailto':
                        href = `mailto:${link}`;
                        break;

                    case 'tel':
                        href = `tel:${link}`;
                        break;
                }
            }

            return href;
        },
        target() {
            return get(this.hyperlink, 'target') || '_self';
        },
        path() {
            if (this.hyperlink.type === 'internal' && get(this.hyperlink, 'menuItem.path'))
                return this.hyperlink.menuItem.path;
            else return false;
        },
        finalLink() {
            if (this.path) {
                return this.path;
            } else if (this.href) {
                return this.href;
            } else {
                return false;
            }
        }

    }
};
</script>

<style lang="sass" scoped>
    @import "~@/assets/sass/variables"
    .preview-image
        @apply relative transition-opacity duration-500 ease-in-out
        .image-wrap img
            max-height: 180px
            margin: 0 auto
        .info-box .bg
            @apply absolute w-full h-full top-0 left-0 opacity-0
            background-color: rgba($color-primary, .9)
            transition: opacity 0.4s cubic-bezier(.15,.75,.5,1) 0s
        .box
            @apply absolute w-full h-full top-0 left-0 opacity-0 flex-col justify-center items-center text-white font-bold p-3
            .href
                @apply text-center
            .desc
                @apply font-normal text-center
                p:empty
                    @apply hidden

    .preview-image:hover .info-box
        .bg, .box
            @apply flex absolute opacity-100


    
</style>
